/**
 * @file Helper functions for Compose component
 */

import { ComposeNodeId, ComposeNodeType } from '../../containers/compose/props';
import {
  TemplateBasicFragment,
  TopicBasicFragment,
} from '../../generated/graphql';
import translate from '../../i18n/translate';
import { ComposeTypeParam } from '../../models/pageParams';

/**
 * Get which title to use for the compose form
 *
 * @param composeType The type of object compose is for (post or task)
 * @param nodeId      The ID of the node (template) being edited (null for creation)
 * @param nodeType    Whether the compose form is being used for messages or templates
 *
 * @returns           The translation key for title
 */
export const getComposeTitle = (
  composeType: ComposeTypeParam | null,
  nodeId: ComposeNodeId,
  nodeType: ComposeNodeType,
): string => {
  const isPost = composeType === 'post';

  if (nodeType === 'MESSAGE') {
    return translate(isPost ? 'POSTS__NEW' : 'TASKS__NEW');
  }

  if (nodeId === null) {
    return translate(isPost ? 'POST_TEMPLATES__NEW' : 'TASK_TEMPLATES__NEW');
  }

  return translate('SETTINGS__TEMPLATES__COMPOSE__EDIT');
};

type TemplateSelectArgs = {
  currentTemplateId: TemplateBasicFragment['id'] | null;
  newTemplateId: TemplateBasicFragment['id'];
  onClose?: () => void;
  requestTemplateChange: (id: TemplateBasicFragment['id']) => void;
  requestTemplateRemove: () => void;
  requestTemplateSave: () => void;
};

/**
 * Handles selecting or unselecting a template.
 * If the new template is already selected, it will be removed.
 *
 * @param props                       Props passed to the component
 * @param props.currentTemplateId     Currently selected template ID
 * @param props.newTemplateId         ID of the template to select
 * @param props.requestTemplateChange Callback to change the selected template
 * @param props.requestTemplateRemove Callback to remove the selected template
 * @param props.requestTemplateSave   Callback to save the selected template
 * @param props.onClose               Optional callback to close popover after selection
 */
export function handleTemplateSelect({
  currentTemplateId,
  newTemplateId,
  requestTemplateChange,
  requestTemplateRemove,
  requestTemplateSave,
  onClose,
}: TemplateSelectArgs) {
  // Re-selecting the same topic does nothing – subpage stays open
  if (newTemplateId === currentTemplateId) return;
  if (currentTemplateId === newTemplateId) {
    requestTemplateRemove();
  } else {
    requestTemplateChange(newTemplateId);
  }

  requestTemplateSave();

  onClose?.();
}

type TopicSelectArgs = {
  currentTopicId: TopicBasicFragment['id'] | null;
  newTopicId: TopicBasicFragment['id'];
  requestTopicChange: (topicId: TopicBasicFragment['id']) => void;
  requestTopicRemove?: () => void;
  requestTopicSave: () => void;
  onClose?: () => void;
};

/**
 * Handles selecting or unselecting a topic.
 * If the new topic is already selected, it will be removed.
 *
 * @param props                    Props passed to the container
 * @param props.currentTopicId     The currently selected topic ID
 * @param props.newTopicId         The ID of the topic to select
 * @param props.requestTopicChange Callback to update the selected topic
 * @param props.requestTopicRemove Optional callback to remove the selected topic
 * @param props.requestTopicSave   Callback to save the topic selection
 * @param props.onClose            Optional callback to close the UI after selection
 */
export function handleTopicSelect({
  currentTopicId,
  newTopicId,
  requestTopicChange,
  requestTopicRemove,
  requestTopicSave,
  onClose,
}: TopicSelectArgs) {
  // Re-selecting the same template does nothing – subpage stays open
  if (currentTopicId === newTopicId) return;
  if (currentTopicId === newTopicId) {
    requestTopicRemove?.();
  } else {
    requestTopicChange(newTopicId);
  }

  requestTopicSave();

  if (onClose) {
    onClose();
  }
}
